import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { ApiErrorCode } from 'src/app/interfaces';

/** Action the user chose in the dialog; the caller maps it to navigation. */
export type StoreOpenBlockedAction = 'add' | 'support' | 'payDebt';

export interface PaymentMethodErrorDialogData {
  /** Machine-readable API error code (ApiErrorCode). Drives the copy and CTAs. */
  code?: string;
  /**
   * Backend human message. Used for analytics/logging ONLY and never displayed — for the
   * fraud codes (banned / blacklisted card) it would reveal the block reason.
   */
  message?: string;
  /** Legacy: HTTP status number, kept for backwards-compatible analytics. */
  errorCode?: number;
}

/** Resolved view model for a given error code. */
interface BlockedView {
  titleKey: string;
  bodyKey: string;
  /** Extra bullet reasons + instruction — only the legacy/default variant uses these. */
  reasonKeys?: string[];
  instructionKey?: string;
  primaryLabelKey: string;
  primaryAction: StoreOpenBlockedAction;
  secondaryLabelKey?: string;
  secondaryAction?: StoreOpenBlockedAction;
}

const K = 'components.store_open_blocked';
const LEGACY = 'components.payment_method_error_dialog';

@Component({
  selector: 'app-payment-method-error-dialog',
  templateUrl: './payment-method-error-dialog.component.html',
  styleUrls: ['./payment-method-error-dialog.component.scss']
})
export class PaymentMethodErrorDialogComponent {

  view: BlockedView;

  constructor(
    public dialogRef: MatDialogRef<PaymentMethodErrorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PaymentMethodErrorDialogData,
    private analytics: AnalyticsService
  ) {
    this.view = this.buildView(data?.code);
    // Track when dialog is shown (code = machine code; errorCode = legacy HTTP status).
    this.analytics.logEvent('payment_method_error_shown', {
      code: data?.code ?? 'none',
      errorCode: data?.errorCode ?? 402
    });
  }

  /**
   * Resolve title/body/CTAs for the given error code. Copy comes exclusively from app i18n
   * keyed by the code — never from the backend message — so we don't reveal the block reason
   * for the fraud codes (banned / blacklisted card).
   */
  private buildView(code?: string): BlockedView {
    switch (code) {
      case ApiErrorCode.USER_BANNED:
        return {
          titleKey: `${K}.banned.title`,
          bodyKey: `${K}.banned.body`,
          primaryLabelKey: `${K}.actions.support`,
          primaryAction: 'support'
        };
      case ApiErrorCode.CARD_BANNED:
        return {
          titleKey: `${K}.card_blacklisted.title`,
          bodyKey: `${K}.card_blacklisted.body`,
          primaryLabelKey: `${K}.actions.add`,
          primaryAction: 'add'
        };
      case ApiErrorCode.OWN_DEBT:
        return {
          titleKey: `${K}.own_debt.title`,
          bodyKey: `${K}.own_debt.body`,
          primaryLabelKey: `${K}.actions.add`,
          primaryAction: 'add',
          secondaryLabelKey: `${K}.actions.pay_debt`,
          secondaryAction: 'payDebt'
        };
      case ApiErrorCode.LINKED_DEBT:
        return {
          titleKey: `${K}.linked_debt.title`,
          bodyKey: `${K}.linked_debt.body`,
          primaryLabelKey: `${K}.actions.support`,
          primaryAction: 'support'
        };
      default:
        // Legacy generic payment-method problem (402003 / 402004 / no code).
        return {
          titleKey: `${LEGACY}.title`,
          bodyKey: `${LEGACY}.message`,
          reasonKeys: [`${LEGACY}.reason_pending`, `${LEGACY}.reason_validation`],
          instructionKey: `${LEGACY}.instruction`,
          primaryLabelKey: `${LEGACY}.add_button`,
          primaryAction: 'add'
        };
    }
  }

  onPrimary(): void {
    this.analytics.logEvent('payment_method_error_action', { action: this.view.primaryAction });
    this.dialogRef.close(this.view.primaryAction);
  }

  onSecondary(): void {
    if (!this.view.secondaryAction) return;
    this.analytics.logEvent('payment_method_error_action', { action: this.view.secondaryAction });
    this.dialogRef.close(this.view.secondaryAction);
  }

  onCancel(): void {
    this.analytics.logEvent('payment_method_error_dismissed');
    this.dialogRef.close(undefined);
  }
}
